import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'

// Styles
import { hsla } from '../../styles/colors'
import { mqFrom } from '../../styles/mediaQueries'
import { applyTextStyle } from '../../styles/typography'
import tagBaseStyle from './TagBase'
import { fastTransition } from '../../styles/effects'

export interface IPointerPosition {
  x?: number
  y?: number
}

/**
 * Where an anchored tooltip sits: centred on the element, at its bottom edge,
 * or at its top edge for a control that stands on the thing it describes and
 * would otherwise cover it.
 *
 * Page coordinates rather than viewport ones, because `HoverText` portals into
 * `document.body` and is positioned against the document.
 */
export const anchorPosition = (
  rect: DOMRect,
  edge: 'top' | 'bottom' = 'bottom'
): IPointerPosition => ({
  x: Math.round(rect.left + rect.width / 2 + window.scrollX),
  y: Math.round((edge === 'top' ? rect.top : rect.bottom) + window.scrollY),
})

interface IHoverText {
  text: string
  position: IPointerPosition
  // When set, the tooltip is centered horizontally on `position` and dropped
  // straight below it (use the anchor element's bottom-center), instead of
  // trailing the cursor to the right — which can overflow the screen edge.
  anchored?: boolean
  // With `anchored`: stand above the anchor rather than below it — pass its
  // top-center. For a control that sits on top of the thing it acts on, where a
  // tooltip below would cover exactly what you are pointing at.
  above?: boolean
}

interface IProps {
  jobLevel?: string
  jobTitle?: string
  content?: React.ReactNode
  hoverText?: string
  color?: string
  textColor?: string
  // Center the tooltip below the element instead of trailing the cursor.
  anchored?: boolean
  className?: string
}

export const HoverText: React.FC<IHoverText> = ({
  text,
  position,
  anchored,
  above,
}) => {
  if (!document?.body) return null

  return ReactDOM.createPortal(
    <InfoText pointerPosition={position} anchored={anchored} above={above}>
      {text}
    </InfoText>,
    document.body
  )
}

const JobTag = (props: IProps) => {
  const [pointerPosition, setPointerPosition] = useState<IPointerPosition>({})

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (!props.hoverText) return
    if (props.anchored) {
      // Anchor centered below the element (its bottom-centre), not the cursor.
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
      setPointerPosition(anchorPosition(rect))
      return
    }
    setPointerPosition({
      x: Number(e.pageX.toFixed(0)),
      y: Number(e.pageY.toFixed(0)),
    })
  }

  const handleMouseLeave = () => {
    if (!props.hoverText) return
    setPointerPosition({})
  }
  return (
    <StyledJobTag
      className={props.className}
      color={props.color}
      hoverable={!!props.hoverText}
      textColor={props.textColor}
      onPointerEnter={handleMouseEnter}
      onPointerLeave={handleMouseLeave}
    >
      {props.content && props.content}
      {props.jobLevel &&
        props.jobTitle &&
        props.jobLevel + ' • ' + props.jobTitle}
      {props.hoverText && pointerPosition.x && pointerPosition.y && (
        <HoverText
          text={props.hoverText}
          position={pointerPosition}
          anchored={props.anchored}
        />
      )}
    </StyledJobTag>
  )
}

const StyledJobTag = styled.strong<{
  color?: string
  textColor?: string
  hoverable?: boolean
}>`
  ${tagBaseStyle}
  display: inline-flex;
  background-color: ${(props) =>
    props.color || props.theme.colors.primaryLight};
  color: ${({ textColor, theme }) => textColor || theme.colors.text};
  font-weight: normal;
  transition: background-color ${fastTransition};
  align-items: center;
  cursor: ${({ hoverable }) => (hoverable ? 'pointer' : 'default ')};
`

const InfoText = styled.span<{
  pointerPosition: IPointerPosition
  anchored?: boolean
  above?: boolean
}>`
  ${applyTextStyle('caption')}
  position: absolute;
  z-index: 1101;
  top: ${({ pointerPosition: { y } }) => y && y + 24}px;
  left: 50%;
  transform: translateX(-50%);
  margin-right: 32px;
  box-shadow: 0px 6px 16px 8px ${hsla('primaryDark', 0.04)};
  border: 1px solid ${({ theme }) => theme.colors.text};
  border-radius: 2px;
  box-sizing: border-box;
  padding: 6px 12px;
  background: ${({ theme }) => hsla(theme.colors.text, 0.5)};
  color: ${({ theme }) => theme.colors.lightest};

  ${({ above, anchored, pointerPosition: { x, y } }) =>
    anchored
      ? // Centered on the anchor (x = its centre, y = its bottom, or its top
        // when standing above). Stays put regardless of cursor, so it can't
        // drift off the right edge.
        x &&
        y &&
        `
    top: ${above ? y - 8 : y + 8}px;
    left: ${x}px;
    transform: translateX(-50%)${above ? ' translateY(-100%)' : ''};
    margin-right: 0;
    `
      : mqFrom.S`
    ${({ pointerPosition: { x: px, y: py } }) =>
      px &&
      py &&
      `
    top: ${py - 40}px;
    left: ${px + 4}px;
    `}
    transform: none;
    margin-right: 24px;
  `}
`

export default JobTag
